import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Register.css";

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    surname: "",
    email: "",
    password: "",
    password_confirmation: "",
    telephone_numbers: "",
    whatsapp_number: "",
    date_of_birth: "",
    gender: "",
    nin: "",
    full_house_address: "",
    state_code: "",
    sd_code: "",
    fc_code: "",
    lcda_code: "",
    lg_code: "",
    ward_code: "",
    pu_code: "",
    membership_category: "",
  });

  const [geographicData, setGeographicData] = useState({
    states: [],
    senatorialDistricts: [],
    federalConstituencies: [],
    lcdas: [],
    localGovernments: [],
    wards: [],
    pollingUnits: [],
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const SERVER_URL = "https://tas.itl.ng";
  const navigate = useNavigate();

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/geographic/states`);
      const data = await res.json();
      if (data.status === "success") {
        const validStates = data.data.filter(
          (state) => state.state_code && state.state_code !== "state_code" && state.name !== "name"
        );
        setGeographicData((prev) => ({ ...prev, states: validStates }));
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchSenatorialDistricts = async (stateCode) => {
    if (!stateCode) return;
    try {
      const res = await fetch(
        `${SERVER_URL}/api/v1/geographic/senatorial-districts?state_code=${stateCode}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, senatorialDistricts: data.data }));
      }
    } catch (error) {
      console.error("Error fetching senatorial districts:", error);
    }
  };

  const fetchFederalConstituencies = async (stateCode) => {
    if (!stateCode) return;
    try {
      const res = await fetch(
        `${SERVER_URL}/api/v1/geographic/federal-constituencies?state_code=${stateCode}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, federalConstituencies: data.data }));
      }
    } catch (error) {
      console.error("Error fetching federal constituencies:", error);
    }
  };

  const fetchLCDAs = async (stateCode) => {
    if (!stateCode) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/geographic/lcdas?state_code=${stateCode}`);
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, lcdas: data.data }));
      }
    } catch (error) {
      console.error("Error fetching LCDAs:", error);
    }
  };

  const fetchLocalGovernments = async (stateCode) => {
    if (!stateCode) return;
    try {
      const res = await fetch(
        `${SERVER_URL}/api/v1/geographic/local-governments?state_code=${stateCode}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, localGovernments: data.data }));
      }
    } catch (error) {
      console.error("Error fetching local governments:", error);
    }
  };

  const fetchWards = async (lgCode) => {
    if (!lgCode) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/geographic/wards?lg_code=${lgCode}`);
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, wards: data.data }));
      }
    } catch (error) {
      console.error("Error fetching wards:", error);
    }
  };

  const fetchPollingUnits = async (wardCode) => {
    if (!wardCode) return;
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/geographic/polling-units?ward_code=${wardCode}`);
      const data = await res.json();
      if (data.status === "success") {
        setGeographicData((prev) => ({ ...prev, pollingUnits: data.data }));
      }
    } catch (error) {
      console.error("Error fetching polling units:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "nin" && value.length > 11) return;
    setFormData({ ...formData, [name]: value });

    switch (name) {
      case "state_code":
        fetchSenatorialDistricts(value);
        fetchFederalConstituencies(value);
        fetchLCDAs(value);
        fetchLocalGovernments(value);
        break;
      case "lg_code":
        fetchWards(value);
        break;
      case "ward_code":
        fetchPollingUnits(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    if (formData.password !== formData.password_confirmation) {
      setErrorMessage("Passwords do not match!");
      setLoading(false);
      return;
    }

    const payload = {
      ...formData,
      telephone_numbers: [formData.telephone_numbers],
    };

    try {
      const res = await fetch(`${SERVER_URL}/api/v1/auth/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status === "success") {
        alert("Registration successful!");
        navigate("/login");
      } else {
        setErrorMessage(data.message || "Registration failed. Please check your input.");
      }
    } catch (error) {
      console.error("Network Error:", error);
      setErrorMessage("Registration failed! Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      {/* <a href="/" className="back-link">
        ← Back Homepage
      </a> */}

      <div className="form-container">
        <h2 className="title">Member Registration</h2>
        <p className="subtitle">
          Complete all fields to register as a verified Member. Your Membership
          ID will be auto-generated.
        </p>

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <form onSubmit={handleSubmit} className="registration-form">
          <h3 className="section-title">Personal Information</h3>
          <div className="form-grid">
            <input type="text" name="first_name" placeholder="First name*" value={formData.first_name} onChange={handleChange} required />
            <input type="text" name="surname" placeholder="Surname*" value={formData.surname} onChange={handleChange} required />
            <input type="date" name="date_of_birth" placeholder="Date of Birth*" value={formData.date_of_birth} onChange={handleChange} required />
            <select name="gender" value={formData.gender} onChange={handleChange} required>
              <option value="">Select Gender*</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>

          <h3 className="section-title">Geographic Information</h3>
          <div className="form-grid">
            <select name="state_code" value={formData.state_code} onChange={handleChange} required>
              <option value="">Select State*</option>
              {geographicData.states.map((state) => (
                <option key={state.id} value={state.state_code}>
                  {state.name}
                </option>
              ))}
            </select>
            <select name="sd_code" value={formData.sd_code} onChange={handleChange} required>
              <option value="">Select Senatorial District*</option>
              {geographicData.senatorialDistricts.map((sd) => (
                <option key={sd.id} value={sd.sd_code}>
                  {sd.name}
                </option>
              ))}
            </select>
            <select name="fc_code" value={formData.fc_code} onChange={handleChange} required>
              <option value="">Select Federal Constituency*</option>
              {geographicData.federalConstituencies.map((fc) => (
                <option key={fc.id} value={fc.fc_code}>
                  {fc.name}
                </option>
              ))}
            </select>
            <select name="lcda_code" value={formData.lcda_code} onChange={handleChange} required>
              <option value="">Select LCDA*</option>
              {geographicData.lcdas.map((lcda) => (
                <option key={lcda.id} value={lcda.lcda_code}>
                  {lcda.name}
                </option>
              ))}
            </select>
            <select name="lg_code" value={formData.lg_code} onChange={handleChange} required>
              <option value="">Select Local Government*</option>
              {geographicData.localGovernments.map((lg) => (
                <option key={lg.id} value={lg.lg_code}>
                  {lg.name}
                </option>
              ))}
            </select>
            <select name="ward_code" value={formData.ward_code} onChange={handleChange} required>
              <option value="">Select Ward*</option>
              {geographicData.wards.map((ward) => (
                <option key={ward.id} value={ward.ward_code}>
                  {ward.name}
                </option>
              ))}
            </select>
            <select name="pu_code" value={formData.pu_code} onChange={handleChange} required>
              <option value="">Select Polling Unit*</option>
              {geographicData.pollingUnits.map((pu) => (
                <option key={pu.id} value={pu.pu_code}>
                  {pu.name}
                </option>
              ))}
            </select>
          </div>

          <h3 className="section-title">Contact Information</h3>
          <div className="form-grid">
            <input type="tel" name="telephone_numbers" placeholder="Phone number*" value={formData.telephone_numbers} onChange={handleChange} required />
            <input type="tel" name="whatsapp_number" placeholder="WhatsApp Number*" value={formData.whatsapp_number} onChange={handleChange} required />
            <input type="email" name="email" placeholder="Email Address*" value={formData.email} onChange={handleChange} required />
          </div>

          <h3 className="section-title">Identification Information</h3>
          <div className="form-grid">
            <input type="number" name="nin" placeholder="NIN (11 digits)*" value={formData.nin} onChange={handleChange} required />
            <input type="text" name="full_house_address" placeholder="Full House Address*" value={formData.full_house_address} onChange={handleChange} required />
          </div>

          <h3 className="section-title">Membership</h3>
          <div className="form-grid">
            <select name="membership_category" value={formData.membership_category} onChange={handleChange} required>
              <option value="">Select Category*</option>
              <option value="Volunteer">Volunteer</option>
              <option value="Member">Member</option>
            </select>
          </div>

          <h3 className="section-title">Account Setup</h3>
          <div className="form-grid">
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password@123"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <span onClick={() => setShowPassword(!showPassword)} className="password-toggle">
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="password_confirmation"
                placeholder="Confirm Password*"
                value={formData.password_confirmation}
                onChange={handleChange}
                required
              />
              <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle">
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Registering..." : "Complete Registration"}
          </button>
          <p className="signin-text">
            Already have an account? <Link to="/login">Sign in here</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
